import {
  ClipboardList,
  Inbox,
  LayoutDashboard,
  Library,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router';
import { ClubSwitcher } from '@/components/club-switcher';
import { UserMenu } from '@/components/user-menu';
import { Wordmark } from '@/components/wordmark';
import { useMe, useOrganizations } from '@/lib/queries';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

// Alleen pagina's die bestaan (design-skill); nieuwe items komen per sprint.
const MAIN_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/oefeningen', label: 'Oefeningen', icon: Library },
  { to: '/trainingen', label: 'Trainingen', icon: ClipboardList },
  { to: '/club', label: 'Club', icon: Users },
];
const MODERATION_NAV: NavItem = { to: '/moderatie', label: 'Moderatie', icon: Inbox };
const PLATFORM_NAV: NavItem = { to: '/platform', label: 'Platformbeheer', icon: ShieldCheck };

/** App-shell (design-skill: layout-patterns.md): sidebar ≥lg, bottom-nav op mobiel. */
export function AppShell() {
  const me = useMe();
  const orgs = useOrganizations();
  const roles = me.data?.platformRoles ?? [];
  const isPlatformAdmin = roles.includes('PLATFORM_ADMIN');
  // Zelfde regel als canModerateAnything in de backend; de server beslist.
  const canModerate =
    isPlatformAdmin ||
    roles.includes('PLATFORM_MODERATOR') ||
    (orgs.data?.organizations.some((o) => o.role === 'owner' || o.role === 'admin') ?? false);
  const mainNav = canModerate ? [...MAIN_NAV, MODERATION_NAV] : MAIN_NAV;
  // Maximaal vijf tabs op mobiel; Platformbeheer zit daar in het gebruikersmenu.
  const bottomNav = mainNav;

  return (
    <div className="grid h-dvh lg:grid-cols-[16rem_1fr]">
      <aside
        className="hidden flex-col gap-1 border-r bg-muted/45 p-3 lg:flex"
        aria-label="Hoofdmenu"
      >
        <div className="mb-3 flex h-11 items-center px-2.5">
          <Wordmark to="/" />
        </div>
        <nav className="grid gap-0.5">
          {mainNav.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </nav>
        {isPlatformAdmin && (
          <>
            <p className="px-2.5 pt-4 pb-1.5 text-caption font-medium text-muted-foreground">
              Beheer
            </p>
            <nav className="grid gap-0.5">
              <SidebarLink item={PLATFORM_NAV} />
            </nav>
          </>
        )}
      </aside>

      <div className="flex min-h-0 flex-col overflow-auto">
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-background/88 pr-2 pl-3 backdrop-blur lg:h-16 lg:px-6">
          <Wordmark to="/" className="mr-1 hidden sm:inline-flex lg:hidden" />
          <div className="min-w-0">
            <ClubSwitcher />
          </div>
          <span className="flex-1" />
          {me.data && (
            <UserMenu
              name={me.data.user.name}
              email={me.data.user.email}
              platformLink={isPlatformAdmin}
            />
          )}
        </header>
        <main className="flex-1 pb-20 lg:pb-0">
          <Suspense
            fallback={
              <p role="status" className="p-4 text-muted-foreground">
                Laden…
              </p>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-20 grid h-[calc(4rem+env(safe-area-inset-bottom))] auto-cols-fr grid-flow-col border-t bg-background pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="Hoofdmenu"
      >
        {bottomNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'group flex min-w-0 flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                isActive && 'text-foreground',
              )
            }
          >
            <item.icon
              className="size-[22px] group-aria-[current=page]:text-primary"
              aria-hidden="true"
            />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'group flex h-10 items-center gap-3 rounded-md px-2.5 text-sm font-medium text-muted-foreground transition-colors duration-120 outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
          isActive && 'bg-background text-foreground shadow-xs ring-1 ring-border',
        )
      }
    >
      <item.icon
        className="size-[18px] group-aria-[current=page]:text-primary"
        aria-hidden="true"
      />
      {item.label}
    </NavLink>
  );
}
